import { useMemo } from 'react';

import { useLocation } from '@suntex.core/nav';
import type { PlayerId } from '@suntex.fim/api-client';

/** Strongly typed view over the app's query string and entity path segments. */
export interface AppQueryParams {
  jtfLevel: number | null;
  jtfOperation: number | null;
  jtfWhen: number | null;
  playerId: PlayerId | null;
  grade: number | null;
  year: string | null;
  wdykLevel: string | null;
  bonusLevel: string | null;
  earlyLevel: string | null;
  initialPhase: string | null;
  goalsIndexPhase: string | null;
  gymLevel: string | null;
  skillSetLevel: string | null;
  kasLevel: string | null;
  vifLevel: string | null;
  stickerActivityView: string | null;
  minSticker: string | null;
  trend: string | null;
  isChecked: string | null;
  skillLevel: string | null;
  ctLevel: string | null;
  jtfLevelOptions: string | null;
  rankingLevel: string | null;
  phase: number | null;
  noneUserId: string | null;
  mediaId: string | null;
  friendlyname: string | null;
  height: number | null;
  width: number | null;
  hiddenDatasets: string | null;
}

/** Parse the query string into typed parameters. */
export function parseAppQueryString(search: string): AppQueryParams {
  const params = new URLSearchParams(search);

  function asNumberOrNull<T extends number>(value: string | null): T | null {
    if (!value) return null;
    const n = Number(value);
    return Number.isFinite(n) ? (n as T) : null;
  }

  return {
    jtfLevel: asNumberOrNull(params.get('jtfLevel')),
    jtfOperation: asNumberOrNull(params.get('jtfOperation')),
    jtfWhen: asNumberOrNull(params.get('jtfWhen')),
    playerId: asNumberOrNull(params.get('playerId')),
    grade: params.get('grade') ? Number(params.get('grade')) : null,
    year: params.get('year'),
    wdykLevel: params.get('wdykLevel'),
    bonusLevel: params.get('bonusLevel'),
    earlyLevel: params.get('earlyLevel'),
    initialPhase: params.get('initialPhase'),
    goalsIndexPhase: params.get('goalsIndexPhase'),
    // `gymlevel` (lowercase) is the legacy spelling kept for existing deep links.
    gymLevel: params.get('gymLevel') ?? params.get('gymlevel'),
    skillSetLevel: params.get('skillSetLevel'),
    kasLevel: params.get('kasLevel'),
    vifLevel: params.get('vifLevel'),
    stickerActivityView: params.get('stickerActivityView'),
    minSticker: params.get('minSticker'),
    trend: params.get('trend'),
    isChecked: params.get('isChecked'),
    skillLevel: params.get('skillLevel'),
    ctLevel: params.get('ctLevel'),
    jtfLevelOptions: params.get('jtfLevelOptions'),
    rankingLevel: params.get('rankingLevel'),
    phase: params.get('phase') ? Number(params.get('phase')) : null,
    noneUserId: params.get('noneUserId'),
    mediaId: params.get('mediaId'),
    friendlyname: params.get('friendlyname'),
    height: params.get('height') ? Number(params.get('height')) : null,
    width: params.get('width') ? Number(params.get('width')) : null,
    hiddenDatasets: params.get('hiddenDatasets'),
  };
}

/** Access strongly typed query-string parameters for the current location. */
export function useAppQueryString(): AppQueryParams {
  const { search } = useLocation();

  return useMemo(() => parseAppQueryString(search), [search]);
}
